import {
  areAllEmbeddedExpensesReviewChecked,
  buildReviewKeysFromExpenses,
  getExpenseReviewByKey,
} from "../utils/embeddedExpenseReview";
import { formatLicensePlateInput } from "../utils/formatLicensePlate";
import { formatRupiah } from "../utils/formatRupiah";
import {
  isValidStoredDepartureDate,
  isValidStoredDepartureTime,
} from "../utils/orderDepartureDateTime";

export enum OrderStatus {
  OPEN = "open",
  READY_FOR_REVIEW = "ready_for_review",
  IN_REVIEW = "in_review",
  REVISION_REQUIRED = "revision_required",
  VERIFIED = "verified",
  APPROVED = "approved",
  APPROVED_WITH_OVERRIDE = "approved_with_override",
  COMPLETED = "completed",
}

export type OrderExpenseType = "uang_jalan" | "mob" | "taktis" | "registration" | "additional";

export type SingletonOrderExpenseType = Exclude<OrderExpenseType, "additional">;

export const SINGLETON_ORDER_EXPENSE_TYPES: readonly SingletonOrderExpenseType[] = [
  "uang_jalan",
  "mob",
  "taktis",
  "registration",
] as const;

export interface OrderExpenseLine {
  id?: string;
  type: OrderExpenseType;
  amount: number;
  notes?: string;
  overrideAmount?: number;
  review?: OrderExpenseReviewState;
}

export interface OrderExpenseReviewState {
  checked: boolean;
  needsRevision: boolean;
}

/** @deprecated Prefer OrderExpenseLine; kept for form-adjacent typing of amount+notes rows. */
export interface OrderExpenseAdditional {
  amount: number;
  notes: string;
}

export interface OrderRouteCity {
  id: string;
  name?: string;
}

export const ORDER_EXPENSE_TYPE_LABEL_KEYS: Record<SingletonOrderExpenseType, string> = {
  uang_jalan: "orders.expenseUangJalan",
  mob: "orders.expenseMob",
  taktis: "orders.expenseTaktis",
  registration: "orders.expenseRegistration",
};

export const findExpenseByType = (
  expenses: OrderExpenseLine[] | undefined,
  type: OrderExpenseType,
): OrderExpenseLine | undefined => {
  return (expenses ?? []).find((line) => line.type === type);
};

export const getAdditionalExpenses = (
  expenses: OrderExpenseLine[] | undefined,
): OrderExpenseLine[] => {
  return (expenses ?? []).filter((line) => line.type === "additional");
};

export const normalizeSingletonExpenses = (expenses: OrderExpenseLine[]): OrderExpenseLine[] => {
  const singletons = SINGLETON_ORDER_EXPENSE_TYPES.map((type) => {
    const existing = findExpenseByType(expenses, type);
    return existing ?? { type, amount: 0 };
  });

  return [...singletons, ...getAdditionalExpenses(expenses)];
};

export const buildExpenseReviewKey = (line: OrderExpenseLine): string => {
  if (line.type === "additional") {
    return `additional-${line.id}`;
  }
  return line.type;
};

export interface OrderHistoryChange {
  field: string;
  before?: unknown;
  after?: unknown;
}

export interface OrderHistory {
  userId: string;
  userName?: string;
  profilePictureUrl?: string | null;
  timestamp: number;
  changes: OrderHistoryChange[];
}

export interface OrderRevisionNote {
  userId: string;
  note: string;
  timestamp: number;
}

export interface OrderExpenseReviewItem {
  key: string;
  checked: boolean;
  needsRevision: boolean;
}

export interface OrderProofAttachment {
  id: string;
  filename: string;
  url: string;
}

export interface Order {
  _id: string;
  readableId: string;
  companyId: string;
  teamOperationsId: string;
  status: OrderStatus;
  uangJalanReportId?: string;
  uangJalanReportReadableId?: string;
  uangJalanReportStatus?: string | null;
  suratJalan?: {
    _id: string;
    readableId: string;
    status: string;
  } | null;
  departureTimestamp?: string;
  truckId?: string;
  driverId?: string;
  customerId?: string;
  load?: OrderRouteCity[];
  unload?: OrderRouteCity[];
  proofAttachments?: OrderProofAttachment[];
  externalOrderId?: string;
  expenses?: OrderExpenseLine[];
  expenseMobRoute?: OrderRouteCity[];
  note?: string;
  overrideNotes?: string;
  totalApprovedCost: number;
  teamOperations?: { _id: string; name: string };
  truck?: { _id: string; licensePlate: string };
  driver?: { _id: string; name: string; profilePictureUrl?: string | null };
  customer?: { _id: string; name: string };
  revisionNote?: OrderRevisionNote;
  histories?: OrderHistory[];
}

export interface OrderSearchFilters {
  status?: string;
  statuses?: string[];
  teamOperationsId?: string;
}

export interface OrderFormAdditionalExpense {
  amount: string;
  notes: string;
}

export interface OrderFormValues {
  teamOperationsId: string;
  departureDate: string;
  departureTime: string;
  truckId: string;
  driverId: string;
  customerId: string;
  loadCityIds: string[];
  unloadCityIds: string[];
  externalOrderId: string;
  note: string;
  expenseUangJalan: string;
  expenseMob: string;
  expenseTaktis: string;
  expenseRegistration: string;
  expenseMobRouteCityIds: string[];
  expenseAdditional: OrderFormAdditionalExpense[];
}

export interface UpdateOrderPayload {
  departureTimestamp?: string | null;
  truckId?: string | null;
  driverId?: string | null;
  customerId?: string | null;
  load?: { id: string }[] | null;
  unload?: { id: string }[] | null;
  externalOrderId?: string | null;
  note?: string | null;
  expenses?: OrderExpenseLine[];
  expenseMobRoute?: { id: string }[] | null;
  pendingProofKeys?: string[];
  keptProofAttachmentIds?: string[];
}

export interface BuildUpdateOrderPayloadOptions {
  forUpdate?: boolean;
  additionalExpenseIds?: string[];
}

export const splitDepartureTimestamp = (
  departureTimestamp: string,
): { date: string; time: string } => {
  const trimmed = departureTimestamp.trim();
  if (!trimmed) {
    return { date: "", time: "" };
  }

  // API normalized format: "YYYY-MM-DD HH:mm:ss"
  const spaceSeparatedMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})/);
  if (spaceSeparatedMatch) {
    return {
      date: spaceSeparatedMatch[1],
      time: spaceSeparatedMatch[2],
    };
  }

  // Form / ISO format: "YYYY-MM-DDTHH:mm:ss"
  const isoMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (isoMatch) {
    return {
      date: isoMatch[1],
      time: isoMatch[2],
    };
  }

  const dateOnlyMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnlyMatch) {
    return { date: dateOnlyMatch[1], time: "" };
  }

  return { date: "", time: "" };
};

export const combineDepartureTimestamp = (date: string, time: string): string => {
  const trimmedDate = date.trim();
  const trimmedTime = time.trim();

  if (!trimmedDate && !trimmedTime) {
    return "";
  }

  if (!trimmedDate || !trimmedTime) {
    return "";
  }

  const normalizedTime = trimmedTime.length === 5 ? `${trimmedTime}:00` : trimmedTime;

  return `${trimmedDate}T${normalizedTime}`;
};

export const emptyOrderFormValues = (): OrderFormValues => ({
  teamOperationsId: "",
  departureDate: "",
  departureTime: "",
  truckId: "",
  driverId: "",
  customerId: "",
  loadCityIds: [],
  unloadCityIds: [],
  externalOrderId: "",
  note: "",
  expenseUangJalan: "",
  expenseMob: "",
  expenseTaktis: "",
  expenseRegistration: "",
  expenseMobRouteCityIds: [],
  expenseAdditional: [],
});

export const orderToFormValues = (order: Order): OrderFormValues => {
  const { date, time } = splitDepartureTimestamp(order.departureTimestamp || "");
  const uangJalan = findExpenseByType(order.expenses, "uang_jalan");
  const mob = findExpenseByType(order.expenses, "mob");
  const taktis = findExpenseByType(order.expenses, "taktis");
  const registration = findExpenseByType(order.expenses, "registration");

  return {
    teamOperationsId: order.teamOperationsId,
    departureDate: date,
    departureTime: time,
    truckId: order.truckId || "",
    driverId: order.driverId || "",
    customerId: order.customerId || "",
    loadCityIds: order.load?.map((city) => city.id) || [],
    unloadCityIds: order.unload?.map((city) => city.id) || [],
    externalOrderId: order.externalOrderId || "",
    note: order.note || "",
    expenseUangJalan: uangJalan?.amount.toString() || "",
    expenseMob: mob?.amount.toString() || "",
    expenseTaktis: taktis?.amount.toString() || "",
    expenseRegistration: registration?.amount.toString() || "",
    expenseMobRouteCityIds: order.expenseMobRoute?.map((city) => city.id) || [],
    expenseAdditional: getAdditionalExpenses(order.expenses).map((item) => ({
      amount: item.amount.toString(),
      notes: item.notes || "",
    })),
  };
};

const parseOptionalNumber = (value: string): number | undefined => {
  if (value.trim() === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parseFormNumberForUpdate = (value: string): number | null => {
  if (value.trim() === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const buildExpensesFromFormValues = (
  values: OrderFormValues,
  options: { forUpdate: boolean; additionalExpenseIds?: string[] },
): OrderExpenseLine[] => {
  const expenses: OrderExpenseLine[] = [];

  const pushSingleton = (type: SingletonOrderExpenseType, rawValue: string) => {
    if (options.forUpdate) {
      const amount = parseFormNumberForUpdate(rawValue);
      expenses.push({ type, amount: amount === null || amount < 0 ? 0 : amount });
      return;
    }

    const amount = parseOptionalNumber(rawValue);
    expenses.push({ type, amount: amount === undefined || amount < 0 ? 0 : amount });
  };

  pushSingleton("uang_jalan", values.expenseUangJalan);
  pushSingleton("mob", values.expenseMob);
  pushSingleton("taktis", values.expenseTaktis);
  pushSingleton("registration", values.expenseRegistration);

  const additionalRows = values.expenseAdditional.filter(
    (entry) => entry.amount.trim() !== "" || entry.notes.trim() !== "",
  );
  additionalRows.forEach((row, index) => {
    const line: OrderExpenseLine = {
      type: "additional",
      amount: Number(row.amount),
      notes: row.notes.trim(),
    };
    const id = options.additionalExpenseIds?.[index];
    if (id) {
      line.id = id;
    }
    expenses.push(line);
  });

  return expenses;
};

export interface CreateOrderPayload extends UpdateOrderPayload {
  teamOperationsId?: string;
}

export const buildUpdateOrderPayload = (
  values: OrderFormValues,
  options: BuildUpdateOrderPayloadOptions = {},
): UpdateOrderPayload => {
  const payload: UpdateOrderPayload = {};

  const departureTimestamp = combineDepartureTimestamp(values.departureDate, values.departureTime);
  if (departureTimestamp) {
    payload.departureTimestamp = departureTimestamp;
  }
  if (values.truckId) {
    payload.truckId = values.truckId;
  }
  if (values.driverId) {
    payload.driverId = values.driverId;
  }
  if (values.customerId) {
    payload.customerId = values.customerId;
  }
  if (values.loadCityIds.length >= 1) {
    payload.load = values.loadCityIds.map((id) => ({ id }));
  }
  if (values.unloadCityIds.length >= 1) {
    payload.unload = values.unloadCityIds.map((id) => ({ id }));
  }
  if (values.externalOrderId.trim()) {
    payload.externalOrderId = values.externalOrderId.trim();
  }
  if ((values.note ?? "").trim()) {
    payload.note = (values.note ?? "").trim();
  }

  if (options.forUpdate) {
    payload.departureTimestamp = departureTimestamp || null;
    payload.truckId = values.truckId || null;
    payload.driverId = values.driverId || null;
    payload.customerId = values.customerId || null;
    payload.load = values.loadCityIds.map((id) => ({ id }));
    payload.unload = values.unloadCityIds.map((id) => ({ id }));
    payload.externalOrderId = values.externalOrderId.trim() || null;
    payload.note = (values.note ?? "").trim() || null;
    payload.expenses = buildExpensesFromFormValues(values, {
      forUpdate: true,
      additionalExpenseIds: options.additionalExpenseIds,
    });

    const expenseMob = parseFormNumberForUpdate(values.expenseMob);
    if (expenseMob === null || expenseMob === 0) {
      payload.expenseMobRoute = [];
    } else {
      payload.expenseMobRoute = values.expenseMobRouteCityIds.map((id) => ({ id }));
    }

    return payload;
  }

  const expenses = buildExpensesFromFormValues(values, {
    forUpdate: false,
    additionalExpenseIds: options.additionalExpenseIds,
  });
  if (expenses.length > 0) {
    payload.expenses = expenses;
  }

  const expenseMob = findExpenseByType(expenses, "mob");
  if (expenseMob && expenseMob.amount > 0) {
    payload.expenseMobRoute = values.expenseMobRouteCityIds.map((id) => ({ id }));
  }

  return payload;
};

export const buildCreateOrderPayload = (
  values: OrderFormValues,
  teamOperationsId: string,
): CreateOrderPayload => ({
  teamOperationsId,
  ...buildUpdateOrderPayload(values),
});

export interface ValidateOrderFormOptions {
  requireTeam?: boolean;
  isEdit?: boolean;
}

export const validateOrderForm = (
  values: OrderFormValues,
  options: ValidateOrderFormOptions = {},
): string[] => {
  const errors: string[] = [];

  if (options.requireTeam && !values.teamOperationsId) {
    errors.push("orders.validation.teamRequired");
  }
  if (!options.isEdit) {
    if (!values.customerId) {
      errors.push("orders.validation.customerRequired");
    }
    if (values.loadCityIds.length < 1) {
      errors.push("orders.validation.loadRequired");
    }
    if (values.unloadCityIds.length < 1) {
      errors.push("orders.validation.unloadRequired");
    }
  }

  const expenseMob = parseOptionalNumber(values.expenseMob) ?? 0;
  if (expenseMob > 0 && values.expenseMobRouteCityIds.length !== 2) {
    errors.push("orders.validation.expenseMobRouteRequired");
  }

  const hasDepartureDate = Boolean(values.departureDate.trim());
  const hasDepartureTime = Boolean(values.departureTime.trim());
  if (hasDepartureDate || hasDepartureTime) {
    if (
      !hasDepartureDate ||
      !hasDepartureTime ||
      !isValidStoredDepartureDate(values.departureDate) ||
      !isValidStoredDepartureTime(values.departureTime)
    ) {
      errors.push("orders.validation.departureInvalid");
    }
  }

  values.expenseAdditional.forEach((row, index) => {
    const hasAmount = row.amount.trim() !== "";
    const hasNotes = row.notes.trim() !== "";
    if (!hasAmount && !hasNotes) {
      return;
    }
    if (!hasNotes) {
      errors.push(`orders.validation.additionalNotesRequired:${index}`);
    }
    if (!hasAmount || Number(row.amount) <= 0) {
      errors.push(`orders.validation.additionalAmountRequired:${index}`);
    }
  });

  return errors;
};

export const getReadyForReviewMissingFieldKeys = (order: Order): string[] => {
  const missing: string[] = [];

  if (!order.departureTimestamp?.trim()) {
    missing.push("orders.validation.departureRequired");
  }
  if (!order.truckId) {
    missing.push("orders.validation.truckRequired");
  }
  if (!order.driverId) {
    missing.push("orders.validation.driverRequired");
  }
  if (!order.customerId) {
    missing.push("orders.validation.customerRequired");
  }
  if (!order.load || order.load.length < 1) {
    missing.push("orders.validation.loadRequired");
  }
  if (!order.unload || order.unload.length < 1) {
    missing.push("orders.validation.unloadRequired");
  }
  if (!order.proofAttachments || order.proofAttachments.length < 1) {
    missing.push("orders.validation.proofImageRequired");
  }
  if (!order.externalOrderId?.trim()) {
    missing.push("orders.validation.externalOrderIdRequired");
  }
  if (!findExpenseByType(order.expenses, "uang_jalan")) {
    missing.push("orders.validation.expenseUangJalanRequired");
  }

  const expenseMob = findExpenseByType(order.expenses, "mob");
  if (
    expenseMob &&
    expenseMob.amount > 0 &&
    (!order.expenseMobRoute || order.expenseMobRoute.length !== 2)
  ) {
    missing.push("orders.validation.expenseMobRouteRequired");
  }

  getAdditionalExpenses(order.expenses).forEach((item, index) => {
    if (!item.notes?.trim()) {
      missing.push(`orders.validation.additionalNotesRequired:${index}`);
    }
    if (!item.amount || item.amount <= 0) {
      missing.push(`orders.validation.additionalAmountRequired:${index}`);
    }
  });

  return missing;
};

export const isOrderReadyForReview = (order: Order): boolean => {
  return getReadyForReviewMissingFieldKeys(order).length === 0;
};

export const ORDER_STATUS_VALUES = Object.values(OrderStatus);

export const ORDER_EDITABLE_STATUSES = new Set<OrderStatus>([
  OrderStatus.OPEN,
  OrderStatus.REVISION_REQUIRED,
]);

export const getReportSelectionBlockReasonKey = (
  item: {
    status: OrderStatus;
    teamOperationsId: string;
    uangJalanReportId?: string | null;
  },
  selectedTeamOperationsId?: string,
): string | null => {
  if (item.uangJalanReportId) {
    return "orders.createReportDisabled.alreadyOnReport";
  }
  if (item.status !== OrderStatus.READY_FOR_REVIEW) {
    return "orders.createReportDisabled.status";
  }
  if (selectedTeamOperationsId && item.teamOperationsId !== selectedTeamOperationsId) {
    return "reports.validation.mixedTeams";
  }
  return null;
};

export const canSelectOrderForReport = (
  order: Order,
  selectedTeamOperationsId?: string,
): boolean => {
  return getReportSelectionBlockReasonKey(order, selectedTeamOperationsId) === null;
};

export const isOrderEditable = (status: OrderStatus): boolean => {
  return ORDER_EDITABLE_STATUSES.has(status);
};

export const isOrderApproved = (status: OrderStatus): boolean => {
  return (
    status === OrderStatus.APPROVED ||
    status === OrderStatus.APPROVED_WITH_OVERRIDE ||
    status === OrderStatus.COMPLETED
  );
};

export const buildOrderExpenseReviewKeys = (order: Order): string[] => {
  return buildReviewKeysFromExpenses(order.expenses ?? []);
};

export const getOrderExpenseReviewItem = (order: Order, key: string): OrderExpenseReviewItem => {
  const review = getExpenseReviewByKey(order.expenses ?? [], key);
  return {
    key,
    checked: review.checked,
    needsRevision: review.needsRevision,
  };
};

export const areAllOrderExpenseReviewItemsChecked = (order: Order): boolean => {
  return areAllEmbeddedExpensesReviewChecked(order.expenses ?? []);
};

const isOrderRevisionNote = (value: unknown): value is OrderRevisionNote => {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as OrderRevisionNote).note === "string" &&
    (value as OrderRevisionNote).note.length > 0
  );
};

const normalizeRevisionNote = (
  revisionNote: unknown,
  legacyRevisionNotes: unknown,
): OrderRevisionNote | undefined => {
  if (isOrderRevisionNote(revisionNote)) {
    return revisionNote;
  }

  if (!Array.isArray(legacyRevisionNotes) || legacyRevisionNotes.length === 0) {
    return undefined;
  }

  const latest = [...legacyRevisionNotes]
    .filter(isOrderRevisionNote)
    .sort((a, b) => b.timestamp - a.timestamp)[0];

  return latest;
};

export const mapOrderResponse = (response: any): Order => {
  return {
    _id: response._id,
    readableId: response.readableId,
    companyId: response.companyId,
    teamOperationsId: response.teamOperationsId,
    status: response.status,
    uangJalanReportId: response.uangJalanReportId,
    uangJalanReportReadableId: response.uangJalanReportReadableId,
    uangJalanReportStatus: response.uangJalanReportStatus ?? null,
    suratJalan: response.suratJalan
      ? {
          _id: response.suratJalan._id,
          readableId: response.suratJalan.readableId,
          status: response.suratJalan.status,
        }
      : null,
    departureTimestamp: response.departureTimestamp,
    truckId: response.truckId,
    driverId: response.driverId,
    customerId: response.customerId,
    load: response.load,
    unload: response.unload,
    proofAttachments: Array.isArray(response.proofAttachments) ? response.proofAttachments : [],
    externalOrderId: response.externalOrderId,
    expenses: normalizeSingletonExpenses(Array.isArray(response.expenses) ? response.expenses : []),
    expenseMobRoute: response.expenseMobRoute,
    note: response.note,
    overrideNotes: response.overrideNotes,
    totalApprovedCost: response.totalApprovedCost ?? 0,
    teamOperations: response.teamOperations,
    truck: response.truck,
    driver: response.driver,
    customer: response.customer,
    revisionNote: normalizeRevisionNote(response.revisionNote, response.revisionNotes),
    histories: response.histories ?? response.statusHistories ?? [],
  };
};

export const mapOrdersResponse = (response: any): Order[] => {
  if (!response.data || !Array.isArray(response.data?.orders)) {
    return [];
  }
  return response.data.orders.map(mapOrderResponse);
};

export const calculateOrderExpenseTotal = (order: Order): number => {
  return (order.expenses ?? []).reduce((sum, line) => sum + (line.amount ?? 0), 0);
};

export const formatOrderRoute = (route?: OrderRouteCity[]): string => {
  if (!route?.length) {
    return "—";
  }
  return route.map((city) => city.name || city.id).join(" → ");
};

export const formatOrderLocations = (locations?: OrderRouteCity[]): string =>
  formatOrderRoute(locations);

const isRouteCityArray = (value: unknown): value is OrderRouteCity[] => {
  return (
    Array.isArray(value) &&
    value.every((item) => typeof item === "object" && item !== null && "id" in item)
  );
};

const isExpenseLineArray = (value: unknown): value is OrderExpenseLine[] => {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "type" in item &&
        "amount" in item &&
        typeof (item as OrderExpenseLine).amount === "number",
    )
  );
};

export const formatOrderHistoryValue = (
  field: string,
  value: unknown,
  t: (key: string) => string,
): string => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }

  if (field === "status" && typeof value === "string") {
    return t(value);
  }

  if (field === "truckId" && typeof value === "string") {
    return formatLicensePlateInput(value);
  }

  if (typeof value === "number") {
    return value.toLocaleString();
  }

  if (typeof value === "string") {
    return value;
  }

  if (
    (field === "load" || field === "unload" || field === "expenseMobRoute") &&
    isRouteCityArray(value)
  ) {
    return formatOrderRoute(value);
  }

  if (field === "expenses" && isExpenseLineArray(value)) {
    if (value.length === 0) {
      return "—";
    }
    return value
      .map((item) => {
        const label =
          item.type === "additional"
            ? item.notes || "additional"
            : t(ORDER_EXPENSE_TYPE_LABEL_KEYS[item.type as SingletonOrderExpenseType] || item.type);
        const amount = formatRupiah(item.amount);
        if (item.overrideAmount !== undefined && item.overrideAmount !== null) {
          return `${label}: ${amount} → ${formatRupiah(item.overrideAmount)}`;
        }
        return `${label}: ${amount}`;
      })
      .join("; ");
  }

  if (
    field.startsWith("expenses.") &&
    typeof value === "object" &&
    value !== null &&
    "amount" in value
  ) {
    const item = value as OrderExpenseLine;
    const amount = formatRupiah(Number(item.amount));
    const override =
      item.overrideAmount !== undefined && item.overrideAmount !== null
        ? formatRupiah(Number(item.overrideAmount))
        : undefined;
    if (item.type === "additional") {
      const notes = item.notes || "";
      const withNotes = notes ? `${amount} (${notes})` : amount;
      return override ? `${withNotes} → ${override}` : withNotes;
    }
    return override ? `${amount} → ${override}` : amount;
  }

  return JSON.stringify(value);
};

export const formatOrderHistoryFieldLabel = (field: string, t: (key: string) => string): string => {
  if (field.startsWith("expenses.")) {
    const expenseKey = field.slice("expenses.".length);
    if (expenseKey.startsWith("additional-")) {
      return t("orders.additionalExpenses");
    }
    const labelKey = ORDER_EXPENSE_TYPE_LABEL_KEYS[expenseKey as SingletonOrderExpenseType];
    return labelKey ? t(labelKey) : expenseKey;
  }

  const labels: Record<string, string> = {
    status: "Status",
    revisionNote: "orders.revisionNote",
    departureTimestamp: "Departure",
    truckId: "License Plate",
    driverId: "DRIVER",
    customerId: "Customer",
    load: "orders.load",
    unload: "orders.unload",
    proofImage: "orders.proofImage",
    externalOrderId: "No. LT/POD",
    note: "orders.note",
    expenses: "orders.expenses",
    expenseMobRoute: "Route",
    overrideNotes: "orders.overrideNotes",
  };

  const labelKey = labels[field];
  return labelKey ? t(labelKey) : field;
};

export const sortOrderHistoriesDesc = (histories: OrderHistory[]): OrderHistory[] => {
  return [...histories].sort((a, b) => b.timestamp - a.timestamp);
};
